import React, {
    createContext,
    useState,
    useEffect,
    useRef,
    useContext,
    useCallback,
} from 'react';
import api from '../services/api';
import endpoints from '../utils/commonVariables/endpoints';
import accountInformationViewModel from '../utils/viewModelStructures/accountInformation';
import { viewModelEntities } from '../utils/commonVariables/entityKeys';
import { useIdHotelOwner } from './HotelOwnerContext';
import {
    AccountInformationState,
    ContactState,
    EditableContactField,
    editableContactFields,
    ProfileViewMode,
} from '../types/profile/profileTypes';
import { adaptAccountInformationDDBBToState } from '../services/profile/accountInformationAdapters';
import { useNotification } from './NotificationsContext';
import { FeedbackMessages } from '../utils/commonVariables/messages';
import { getRandomNumber } from '../utils/usefulFunctions';
import { isAdded } from '../utils/collectionPersistence';

export interface AccountInformationContextType {
    accountInfo: AccountInformationState;

    setAccountInfo: React.Dispatch<
        React.SetStateAction<AccountInformationContextType['accountInfo']>
    >;
    viewMode: ProfileViewMode;
    handleViewModeChange: (mode: ProfileViewMode) => void;
    isLoadingAccountInformation: boolean;
    setIsLoadingAccountInformation: React.Dispatch<
        React.SetStateAction<boolean>
    >;
    detailContact: ContactState | null;
    handleDetailContact: (contact?: ContactState) => void;
    handleEditContact: (key: EditableContactField, value: string) => void;
}

export const ContextAccountInformation = createContext<
    AccountInformationContextType | undefined
>(undefined);

export const ProviderAccountInformation = (props: {
    children: React.ReactNode;
}) => {
    const { children } = props;
    const IdHotelOwner = useIdHotelOwner();
    const { setErrorMessage } = useNotification();
    const notifyError = useRef(setErrorMessage);
    useEffect(() => {
        notifyError.current = setErrorMessage;
    }, [setErrorMessage]);

    const CONTACT_INITIAL_STATE: ContactState = {
        Id: `-${getRandomNumber(1, 1000000000)}`,
        ContactType: '',
        Name: '',
        Email: '',
        Phone: '',
        Position: '',
        Notes: '',
        EntityId: 0,
        EntityName: '',
        ResponseTime: '',
        __isAdded: true,
        __isChanged: true,
    };

    const INITIAL_STATE: AccountInformationState = {
        Id: IdHotelOwner as string,
        Name: '',
        Email: '',
        ContactPerson: '',
        Phone: '',
        IdOwnerType: 0,
        OwnerType: '',
        OwnerTypes: [],
        IdCountry: 0,
        Country: '',
        CountryCode: '',
        DateSigned: null,
        HotelChain: '',
        IdCurrency: 0,
        Currency: '',
        Contacts: [],
    };
    const [accountInfo, setAccountInfo] =
        useState<AccountInformationState>(INITIAL_STATE);

    useEffect(() => {
        let active = true;
        const getAccountInformation = async () => {
            setIsLoadingAccountInformation(true);
            setDetailContact(null);
            setViewMode(ProfileViewMode.READ_ONLY);
            try {
                const endpoint = endpoints.viewModel(
                    viewModelEntities.HotelOwner,
                    IdHotelOwner
                );
                const { data } = await api.post(
                    endpoint,
                    accountInformationViewModel
                );
                if (!active) {
                    return;
                }
                if (data.Success) {
                    setAccountInfo(
                        adaptAccountInformationDDBBToState(data.Result)
                    );
                } else {
                    notifyError.current(FeedbackMessages.COMMON_ERROR);
                }
            } catch (error) {
                if (active) {
                    notifyError.current(FeedbackMessages.COMMON_ERROR);
                }
            } finally {
                if (active) {
                    setIsLoadingAccountInformation(false);
                }
            }
        };
        getAccountInformation();
        return () => {
            active = false;
        };
    }, [IdHotelOwner]);

    const [isLoadingAccountInformation, setIsLoadingAccountInformation] =
        useState(false);
    const [viewMode, setViewMode] = useState(ProfileViewMode.READ_ONLY);

    const handleViewModeChange = useCallback((mode: ProfileViewMode) => {
        setViewMode(mode);
    }, []);

    const [detailContact, setDetailContact] = useState<ContactState | null>(
        null
    );
    const initialContact = useRef<ContactState | null>(null);
    const handleDetailContact = (contact?: ContactState) => {
        const selectedContact = contact || CONTACT_INITIAL_STATE;
        initialContact.current = { ...selectedContact };
        setDetailContact(selectedContact);
    };

    const handleEditContact = (key: EditableContactField, value: string) => {
        setDetailContact(previous => {
            if (!previous || previous[key] === value) {
                return previous;
            }
            const updatedContact = { ...previous, [key]: value };
            const original = initialContact.current;
            return {
                ...updatedContact,
                __isChanged:
                    isAdded(updatedContact) ||
                    Boolean(original?.__isChanged) ||
                    editableContactFields.some(
                        field => updatedContact[field] !== original?.[field]
                    ),
            };
        });
    };

    return (
        <ContextAccountInformation.Provider
            value={{
                accountInfo,
                setAccountInfo,
                viewMode,
                handleViewModeChange,
                isLoadingAccountInformation,
                setIsLoadingAccountInformation,
                detailContact,
                handleDetailContact,
                handleEditContact,
            }}
        >
            {children}
        </ContextAccountInformation.Provider>
    );
};

export const useAccountInformation = () => {
    const context = useContext(ContextAccountInformation);
    if (!context) {
        throw new Error(
            'useAccountInformation must be used within an AccountInformationProvider'
        );
    }
    return {
        ...context,
        accountInfo: {
            ...context.accountInfo,
            Contacts: context.accountInfo.Contacts.filter(
                contact => !contact.Deleted
            ),
        },
    };
};
